import { useAppState } from './providers/AppStateProvider'
import { cn } from '@/shared/utils/cn'
import { Icon } from '@/shared/ui/Icon'
import { LeftSidebar } from '@/features/conversations/components/LeftSidebar'
import { ChatThread } from '@/features/chat/components/ChatThread'
import { Composer } from '@/features/chat/components/Composer'
import { HomePromptScreen } from '@/features/chat/components/HomePromptScreen'
import { RightEvidencePanel } from '@/features/sources/components/RightEvidencePanel'
import { SettingsPlaceholder } from '@/features/settings/components/SettingsPlaceholder'
import { DocumentsPage } from '@/features/documents'
import { ReferenceLibraryPage } from '@/features/reference-library'
import {
  DRAFT_CONVERSATION_ID,
  useConversations,
} from '@/features/conversations/ConversationsProvider'

/**
 * Three-column workspace. No top navbar — the sidebar is the only persistent
 * chrome (collapsible to a rail of icons). The right evidence panel can be
 * collapsed entirely; when it is, a single floating toggle is shown.
 *
 * When no conversation is selected (`activeConversationId === DRAFT_CONVERSATION_ID`)
 * the center column renders the prompt-first `<HomePromptScreen />` and the
 * regular thread/composer are not mounted — no fake answer, no fake sources.
 */
export function AppShell() {
  const {
    leftCollapsed,
    rightCollapsed,
    toggleRight,
    settingsOpen,
    activeWorkspace,
    setWorkspace,
  } = useAppState()
  const { activeConversationId, startNewConsultation } = useConversations()

  const isDocuments = activeWorkspace === 'documents'
  const isReferenceLibrary = activeWorkspace === 'reference-library'
  const isWorkspaceFullWidth = isDocuments || isReferenceLibrary
  const isHome = activeConversationId === DRAFT_CONVERSATION_ID
  // Library pages own the full center column; the right evidence panel is
  // suppressed because the catalog has no per-message sources to render.
  const hideAside = isWorkspaceFullWidth

  /**
   * Library "Usar no chat" handler.
   *
   * Always lands the user on the Home/New Consultation prompt with the
   * freshly picked scope in the badge — regardless of whether there was a
   * previously active conversation. The new conversation is only created
   * (server-side) when the user sends the first message, mirroring the
   * "Nova consulta" button. The previous conversation stays untouched.
   */
  const handleLibraryScopeSelected = () => {
    startNewConsultation()
    setWorkspace('chat')
  }

  return (
    <>
      <div
        className={cn('app', {
          'left-collapsed': leftCollapsed,
          'right-collapsed': rightCollapsed || hideAside,
        })}
      >
        <LeftSidebar />

        <main className="chat">
          {/* Right panel toggle only — the sidebar never disappears, so it
              owns its own collapse control. */}
          {!isWorkspaceFullWidth && (
            <button
              type="button"
              className="floating-toggle toggle-right"
              title="Abrir painel de fontes"
              onClick={toggleRight}
            >
              <Icon name="panel-right" />
            </button>
          )}

          {isDocuments ? (
            <DocumentsPage onScopeSelected={handleLibraryScopeSelected} />
          ) : isReferenceLibrary ? (
            <ReferenceLibraryPage />
          ) : isHome ? (
            <div className="relative flex min-h-0 flex-1 flex-col">
              <HomePromptScreen />
            </div>
          ) : (
            <>
              {/* POLISH 1C — the heavy ThreadBar ("Nova consulta" header) was
                  removed from the chat view. New consultation lives in the
                  sidebar (nav-new-chat); rename/delete live on each
                  conversation row (ConversationList row-actions). */}
              <div className="chat-body">
                <ChatThread />
              </div>
              <Composer />
            </>
          )}
        </main>

        {!hideAside && <RightEvidencePanel />}
      </div>

      {settingsOpen && <SettingsPlaceholder />}
    </>
  )
}
